import { useState, useEffect, useCallback } from 'preact/hooks'
import { Seite } from '../app.jsx'
import { api } from '../lib/api.js'
import { schemaLesen, schemaSetzen } from '../lib/theme.js'
import {
  abzeichenAn, abzeichenMoeglich, abzeichenEinschalten, abzeichenAusschalten, setzeAbzeichen,
} from '../lib/abzeichen.js'
import { Laden, Fehler } from '../components/Zustand.jsx'

/**
 * Einstellungen: SRS-Verfahren, App-Symbol und Erscheinungsbild.
 *
 * Die Grenzen für request_retention stehen hier *und* im Server. Doppelt ist
 * hier richtig: der Server begrenzt still, und ein Wert, der beim Speichern
 * springt, wirkt wie ein Fehler.
 */

// Identisch zu FSRS_DEFAULT_W in server/srs/fsrs.js.
const FSRS_STANDARD = [
  0.40255, 1.18385, 3.173, 15.69105, 7.1949, 0.5345, 1.4604, 0.0046, 1.54575, 0.1192, 1.01925,
  1.9395, 0.11, 0.29605, 2.2698, 0.2315, 2.9898, 0.51655, 0.6621,
]

const RETENTION_MIN = 0.7
const RETENTION_MAX = 0.97

const SCHEMATA = [
  { wert: 'system', label: 'System' },
  { wert: 'hell', label: 'Hell' },
  { wert: 'dunkel', label: 'Dunkel' },
]

function begrenze(wert, min, max, ersatz) {
  const zahl = Number(wert)
  if (!Number.isFinite(zahl)) return ersatz
  return Math.min(max, Math.max(min, zahl))
}

export function Einstellungen() {
  const [srs, setSrs] = useState(null)
  const [gewichte, setGewichte] = useState(FSRS_STANDARD.map(String))
  const [stepsText, setStepsText] = useState('1, 3')
  const [laedt, setLaedt] = useState(true)
  const [fehler, setFehler] = useState(null)
  const [meldung, setMeldung] = useState(null)
  const [schema, setSchema] = useState(() => schemaLesen())
  const [abzeichen, setAbzeichen] = useState(() => abzeichenAn())
  const [abzeichenHinweis, setAbzeichenHinweis] = useState(null)
  const [ich, setIch] = useState(null)

  useEffect(() => {
    api.get('/api/me').then(setIch).catch(() => {})
  }, [])

  useEffect(() => {
    api
      .get('/api/settings/srs')
      .then((daten) => {
        setSrs(daten)
        setStepsText((daten.steps ?? [1, 3]).join(', '))
        setGewichte((daten.fsrs_weights ?? FSRS_STANDARD).map(String))
        setLaedt(false)
      })
      .catch((f) => {
        setFehler(f)
        setLaedt(false)
      })
  }, [])

  const speichern = useCallback(() => {
    const rumpf = {
      algorithm: srs.algorithm,
      ease_start: Number(srs.ease_start) || 2.5,
      ease_min: Number(srs.ease_min) || 1.3,
      request_retention: begrenze(srs.request_retention, RETENTION_MIN, RETENTION_MAX, 0.9),
    }

    if (srs.algorithm === 'fsrs') {
      rumpf.fsrs_weights = gewichte.map((g) => Number(g) || 0)
    } else {
      rumpf.steps = stepsText
        .split(',')
        .map((s) => Math.max(1, Math.trunc(Number(s.trim()) || 1)))
        .filter(Boolean)
    }

    setMeldung(null)
    api
      .put('/api/settings/srs', rumpf)
      .then(() => setMeldung('Gespeichert.'))
      .catch((f) => setMeldung(f.message))
  }, [srs, gewichte, stepsText])

  function setzeSchema(wert) {
    setSchema(schemaSetzen(wert))
  }

  /**
   * Abzeichen umschalten. Beim Einschalten fragt Safari nach der Erlaubnis für
   * Mitteilungen — ohne sie zeigt das iPhone kein Abzeichen, auch wenn nie eine
   * Mitteilung verschickt wird.
   */
  async function schalteAbzeichen(an) {
    setAbzeichenHinweis(null)
    if (!an) {
      abzeichenAusschalten()
      setAbzeichen(false)
      return
    }
    const { an: klappt, grund } = await abzeichenEinschalten()
    setAbzeichen(klappt)
    if (grund === 'nicht-unterstuetzt') {
      setAbzeichenHinweis('Dieser Browser kennt keine Zahl am App-Symbol. Auf dem iPhone geht es nur in der installierten App.')
      return
    }
    if (grund === 'abgelehnt') {
      setAbzeichenHinweis('Mitteilungen sind für diese App abgelehnt. In den Systemeinstellungen erlauben, dann hier erneut einschalten.')
      return
    }
    // Sofort die aktuelle Zahl setzen, statt bis zum nächsten Start zu warten.
    api
      .get('/api/topics', {})
      .then((daten) => setzeAbzeichen(daten.due_total))
      .catch(() => setAbzeichenHinweis('Eingeschaltet. Die Zahl erscheint beim nächsten Blick auf die Themenliste.'))
  }

  if (laedt) return <Laden />
  if (fehler) return <Fehler fehler={fehler} />

  const istFsrs = srs.algorithm === 'fsrs'

  return (
    <Seite titel="Einstellungen">
      {meldung ? (
        <p class="zustand" role="status">
          {meldung}
        </p>
      ) : null}

      <section class="einstellungen__block">
        <h2 class="einstellungen__titel">Wiederholungsverfahren</h2>

        <label class="feld">
          <span class="feld__label meta">Algorithmus</span>
          <select
            class="feld__eingabe"
            value={srs.algorithm}
            onChange={(e) => setSrs({ ...srs, algorithm: e.currentTarget.value })}
          >
            <option value="fsrs">FSRS-5</option>
            <option value="custom">SM-2</option>
          </select>
        </label>

        {istFsrs ? (
          <label class="feld">
            <span class="feld__label meta">Zielretention (0,7 bis 0,97)</span>
            <input
              class="feld__eingabe"
              type="number"
              step="0.01"
              min={RETENTION_MIN}
              max={RETENTION_MAX}
              value={srs.request_retention}
              onInput={(e) => setSrs({ ...srs, request_retention: e.currentTarget.value })}
            />
          </label>
        ) : (
          <>
            <label class="feld">
              <span class="feld__label meta">Steps in Tagen, mit Komma getrennt</span>
              <input
                class="feld__eingabe"
                value={stepsText}
                onInput={(e) => setStepsText(e.currentTarget.value)}
              />
            </label>

            <label class="feld">
              <span class="feld__label meta">Kleinster Ease-Faktor</span>
              <input
                class="feld__eingabe"
                type="number"
                step="0.1"
                value={srs.ease_min}
                onInput={(e) => setSrs({ ...srs, ease_min: e.currentTarget.value })}
              />
            </label>
          </>
        )}

        {istFsrs ? (
          <fieldset class="einstellungen__gewichte">
            <legend class="feld__label meta">FSRS-Gewichte</legend>
            <p class="meta einstellungen__warnung">
              Diese Werte bestimmen den Lernstand aller 264 Karten. Ohne Grund nichts ändern.
            </p>
            <div class="einstellungen__gewichte-raster">
              {gewichte.map((wert, i) => (
                <label class="feld" key={i}>
                  <span class="feld__label meta">Gewicht {i + 1}</span>
                  <input
                    class="feld__eingabe zahl"
                    value={wert}
                    onInput={(e) =>
                      setGewichte((alt) =>
                        alt.map((g, j) => (j === i ? e.currentTarget.value : g)),
                      )
                    }
                  />
                </label>
              ))}
            </div>
            <button
              type="button"
              class="knopf knopf--still"
              onClick={() => setGewichte(FSRS_STANDARD.map(String))}
            >
              Standardwerte
            </button>
          </fieldset>
        ) : null}

        <div class="einstellungen__aktionen">
          <button type="button" class="knopf knopf--haupt" onClick={speichern}>
            Speichern
          </button>
        </div>
      </section>

      <section class="einstellungen__block">
        <h2 class="einstellungen__titel">App-Symbol</h2>
        <div class="themenschalter-block">
          <div class="themenschalter">
            <label class="themenschalter__kopf">
              <span class="themenschalter__titel">Fällige Karten als Zahl</span>
              <input
                type="checkbox"
                role="switch"
                class="themenschalter__kippe"
                checked={abzeichen}
                disabled={!abzeichenMoeglich()}
                onChange={(e) => schalteAbzeichen(e.currentTarget.checked)}
              />
            </label>
            <p class="themenschalter__hinweis meta">
              {abzeichen
                ? 'Die Zahl wird nachgezogen, solange die App läuft — beim Öffnen der Themenliste und am Ende einer Sitzung. Über Nacht bleibt der Stand von gestern Abend stehen: dafür bräuchte es Push-Nachrichten, und die App ist nur über Tailscale erreichbar.'
                : 'Zeigt die Zahl der fälligen Karten am Symbol der installierten App. Das iPhone verlangt dafür die Erlaubnis für Mitteilungen — verschickt wird keine.'}
            </p>
          </div>
        </div>
        {abzeichenHinweis ? (
          <p class="zustand" role="status">
            {abzeichenHinweis}
          </p>
        ) : null}
      </section>

      <section class="einstellungen__block">
        <h2 class="einstellungen__titel">Erscheinungsbild</h2>
        <div class="einstellungen__schemata" role="radiogroup" aria-label="Farbschema">
          {SCHEMATA.map(({ wert, label }) => (
            <label class="einstellungen__schema" key={wert}>
              <input
                type="radio"
                name="schema"
                value={wert}
                checked={schema === wert}
                onChange={() => setzeSchema(wert)}
              />
              {label}
            </label>
          ))}
        </div>
      </section>

      <section class="einstellungen__block">
        <h2 class="einstellungen__titel">Konto</h2>
        {ich ? (
          <p class="meta">
            Angemeldet als {ich.name ?? ich.email}
            {ich.name && ich.email ? ` (${ich.email})` : ''}
          </p>
        ) : null}
        <div class="einstellungen__aktionen">
          {/* Ein Link, kein fetch: Abmelden leitet zu Pocket-ID und zurück. */}
          <a class="knopf knopf--still" href="/auth/logout">Abmelden</a>
        </div>
      </section>
    </Seite>
  )
}
